import React, { useState } from "react";
import { RotateCcw } from "lucide-react";
import { BusinessCard } from "../sample.data";

interface BusinessCardProps {
  businessCard: BusinessCard;
  cardImages: {
    front: string;
    back: string;
  };
}

export const BusinessCardSection: React.FC<BusinessCardProps> = ({
  businessCard,
  cardImages,
}) => {
  const [isFlipped, setIsFlipped] = useState(false);

  const placeholder =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1200' height='630'%3E%3Cdefs%3E%3ClinearGradient id='g' x1='0' x2='1'%3E%3Cstop stop-color='%23ffffff'/%3E%3Cstop offset='1' stop-color='%23e5e7eb'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect fill='url(%23g)' width='1200' height='630'/%3E%3C/svg%3E";

  const frontUrl =
    cardImages.front || businessCard.frontImageUrl || placeholder;
  const backUrl = cardImages.back || businessCard.backImageUrl || placeholder;

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  return (
    <div className="w-full">
      {/* Card Container */}
      <div className="flex flex-col items-center space-y-6">
        {/* Flip Card */}
        <div className="relative w-full max-w-sm mx-auto">
          {/* 3D Card Container */}
          <div
            className="relative w-full cursor-pointer select-none"
            style={{
              perspective: "1000px",
              aspectRatio: "1.75", // Standard business card ratio
            }}
            onClick={handleFlip}
          >
            {/* Card Wrapper with 3D Transform */}
            <div
              className="relative w-full h-full transition-all duration-700 ease-in-out"
              style={{
                transformStyle: "preserve-3d",
                transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
                transformOrigin: "center center",
              }}
            >
              {/* Front Side */}
              <div
                className="absolute inset-0 w-full h-full overflow-hidden rounded-xl border border-black/10 bg-gradient-to-br from-black/5 to-black/10 shadow-2xl dark:border-white/10 dark:from-white/5 dark:to-white/10"
                style={{
                  backfaceVisibility: "hidden",
                  WebkitBackfaceVisibility: "hidden",
                }}
              >
                <img
                  alt="Business card front"
                  src={frontUrl}
                  className="h-full w-full object-cover"
                  draggable={false}
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-transparent opacity-30" />

                {/* Tap indicator */}
                <div className="absolute top-2 right-2 bg-black/20 backdrop-blur-sm rounded-full p-1.5 text-white/80">
                  <RotateCcw className="w-3 h-3" />
                </div>
              </div>

              {/* Back Side */}
              <div
                className="absolute inset-0 w-full h-full overflow-hidden rounded-xl border border-black/10 bg-gradient-to-br from-black/5 to-black/10 shadow-2xl dark:border-white/10 dark:from-white/5 dark:to-white/10"
                style={{
                  backfaceVisibility: "hidden",
                  WebkitBackfaceVisibility: "hidden",
                  transform: "rotateY(180deg)",
                }}
              >
                <img
                  alt="Business card back"
                  src={backUrl}
                  className="h-full w-full object-cover"
                  draggable={false}
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-transparent opacity-30" />
              </div>
            </div>
          </div>
        </div>

        {/* Instruction Text */}
        <div className="text-center space-y-3">
          <p className="text-sm font-medium text-black/70 dark:text-white/70 flex items-center justify-center gap-2">
            <RotateCcw className="w-4 h-4" />
            Tap card to flip and see {isFlipped ? "front" : "back"} side
          </p>
        </div>
      </div>
    </div>
  );
};
